import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { DollarSign, ArrowUpRight, Clock, CheckCircle2, Wallet } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

interface UserBalance {
  fomo_balance_cents: number;
  stripe_balance_cents: number;
  lifetime_earned_cents: number;
  lifetime_transferred_cents: number;
  lifetime_paid_out_cents: number;
  payout_threshold_cents: number;
  last_transfer_at: string | null;
  last_payout_at: string | null;
}

const UserBalanceWidget: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [balance, setBalance] = useState<UserBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadBalance();
    }
  }, [user?.id]);

  const loadBalance = async () => {
    try {
      const { data, error } = await supabase
        .from('user_balances')
        .select('*')
        .eq('user_id', user!.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setBalance(data || null);
    } catch (error: any) {
      console.error('Error loading balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!balance || balance.fomo_balance_cents < balance.payout_threshold_cents) {
      toast({
        title: 'Cannot transfer',
        description: `You need $${(balance?.payout_threshold_cents || 2000) / 100} to transfer`,
        variant: 'destructive',
      });
      return;
    }

    setTransferring(true);
    try {
      const { data, error } = await supabase.functions.invoke('process-batch-transfer');

      if (error) throw error;

      toast({
        title: 'Transfer initiated!',
        description: data.message,
      });

      await loadBalance();
    } catch (error: any) {
      toast({
        title: 'Transfer failed',
        description: error.message || 'Failed to process transfer',
        variant: 'destructive',
      });
    } finally {
      setTransferring(false);
    }
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="p-4">
          <div className="animate-pulse h-20 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!balance) {
    return (
      <Card className="w-full bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="p-4 text-center">
          <Wallet className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No earnings yet</p>
          <p className="text-xs text-muted-foreground mt-1">Burn drops to start earning!</p>
        </CardContent>
      </Card>
    );
  }

  const fomoBalance = balance.fomo_balance_cents / 100;
  const stripeBalance = balance.stripe_balance_cents / 100;
  const totalPending = fomoBalance + stripeBalance;
  const threshold = balance.payout_threshold_cents / 100;
  const canTransfer = balance.fomo_balance_cents >= balance.payout_threshold_cents;
  const progress = (balance.fomo_balance_cents / balance.payout_threshold_cents) * 100;

  return (
    <Card className="w-full bg-gradient-to-br from-green-500/5 to-emerald-500/10 border-green-500/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-green-600" />
          Your Earnings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* FOMO Balance (Pending) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-500" />
              <span className="text-sm text-muted-foreground">FOMO Balance</span>
            </div>
            <span className="text-lg font-bold text-orange-600">
              ${fomoBalance.toFixed(2)}
            </span>
          </div>
          
          {/* Progress to threshold */}
          <div className="space-y-1">
            <div className="h-2 bg-background rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-orange-500 to-green-500 transition-all duration-300"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            <div className="text-xs text-muted-foreground text-right">
              ${threshold.toFixed(2)} needed to transfer
            </div>
          </div>
        </div>

        {/* Stripe Balance (Ready to Pay) */}
        {stripeBalance > 0 && (
          <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span className="text-sm text-muted-foreground">Stripe Balance</span>
            </div>
            <span className="text-lg font-semibold text-green-600">
              ${stripeBalance.toFixed(2)}
            </span>
          </div>
        )}

        {/* Transfer Button */}
        <Button
          onClick={handleTransfer}
          disabled={!canTransfer || transferring}
          className="w-full"
          variant={canTransfer ? 'default' : 'outline'}
        >
          {transferring ? (
            <>
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
              Processing...
            </>
          ) : canTransfer ? (
            <>
              <ArrowUpRight className="w-4 h-4 mr-2" />
              Transfer ${fomoBalance.toFixed(2)} to Stripe
            </>
          ) : (
            `Earn ${((threshold - fomoBalance)).toFixed(2)} more`
          )}
        </Button>

        {/* View History */}
        <Dialog open={showHistory} onOpenChange={setShowHistory}>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full text-xs">
              View Transaction History
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Earnings Summary</DialogTitle>
              <DialogDescription>
                Your complete earnings and payout history
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-background rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">Lifetime Earned</div>
                  <div className="text-2xl font-bold">
                    ${(balance.lifetime_earned_cents / 100).toFixed(2)}
                  </div>
                </div>
                <div className="p-4 bg-background rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">Lifetime Paid</div>
                  <div className="text-2xl font-bold">
                    ${(balance.lifetime_paid_out_cents / 100).toFixed(2)}
                  </div>
                </div>
              </div>
              
              {balance.last_transfer_at && (
                <div className="text-xs text-muted-foreground">
                  Last transfer: {new Date(balance.last_transfer_at).toLocaleDateString()}
                </div>
              )}
              
              {balance.last_payout_at && (
                <div className="text-xs text-muted-foreground">
                  Last payout: {new Date(balance.last_payout_at).toLocaleDateString()}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <div className="text-xs text-muted-foreground text-center pt-2 border-t">
          Payouts process via Stripe according to your account schedule
        </div>
      </CardContent>
    </Card>
  );
};

export default UserBalanceWidget;
